import AutorenewIcon from '@mui/icons-material/Autorenew';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { Box, IconButton, Stack } from '@mui/material';
import { Button } from 'src/UIKit/components';
import Tooltip from '@mui/material/Tooltip';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import React, { FC } from 'react';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import GridData from 'src/components/GridData/GridData';
import { TableHeader } from 'src/components/TableHeader/TableHeader';
import TooltipCell from 'src/components/TooltipCell';
import { isActionAllowed } from 'src/helpers/permissions';
import { useApiKeysViewController } from '../../controllers';
import { Apikey } from '../../types';
import { AddApiKeyModal } from '../AddApiKeyModal';
import { settingsTabPanelSx } from '../../helpers';

type ApiKeysViewProps = {
  profileRole: string;
};

export const ApiKeysView: FC<ApiKeysViewProps> = ({ profileRole }) => {
  const {
    isLoading,
    isButtonsDisabled,
    searchQuery,
    sortField,
    page,
    itemsPerPage,
    apikeys,
    changeSearch,
    changeSorting,
    changePagination,
    openCreateModal,
    openEditModal,
    removeApikey,
    regenerateApikey,
    addApiKeyModalController,
  } = useApiKeysViewController(profileRole);
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const formatPermissions = (permissions: { name: string; description: string }[]) => {
    return permissions ? permissions.map(p => p.description).join(', ') : '';
  };

  const formatTableRows = (items: Apikey[]) => {
    return items.map(({ id, name, description, token, permissions, createdAt, updatedAt }) => ({
      id,
      name,
      description,
      token,
      permissions,
      createdAt,
      updatedAt,
      actions: 'edit/delete',
    }));
  };

  return (
    <>
      <AddApiKeyModal {...addApiKeyModalController} />
      <Box sx={settingsTabPanelSx}>
        <GridData
          header={
            <TableHeader
              searchValue={searchQuery}
              onSearchChange={changeSearch}
              actions={
                <Button
                  variant="contained"
                  onClick={openCreateModal}
                  disabled={
                    !isActionAllowed({ authorRole: profileRole }) ||
                    profileRole === 'company_manager'
                  }
                >
                  Create API Key
                </Button>
              }
            />
          }
          isLoading={isLoading}
          rows={formatTableRows(apikeys.items || [])}
          columns={[
            {
              field: 'name',
              headerName: 'Name',
              minWidth: 200,
              editable: false,
              flex: 1,
              renderCell: params => (
                <Stack direction="row" alignItems="center" height="100%">
                  <TooltipCell value={params.value || ''} variant="body2" />
                </Stack>
              ),
            },
            {
              field: 'description',
              headerName: 'Description',
              minWidth: 200,
              editable: false,
              flex: 1,
              renderCell: params => (
                <Stack direction="row" alignItems="center" height="100%">
                  <TooltipCell value={params.value || ''} variant="body2" />
                </Stack>
              ),
            },
            {
              field: 'permissions',
              headerName: 'Permissions',
              minWidth: 100,
              editable: false,
              renderCell: params => (
                <Stack direction="row" alignItems="center" height="100%">
                  <TooltipCell
                    value={formatPermissions(params.value)}
                    variant="body2"
                  />
                </Stack>
              ),
              flex: 1,
            },
            {
              field: 'createdAt',
              headerName: 'Created At',
              minWidth: 100,
              editable: false,
              flex: 1,
              renderCell: r => <Box>{dayjs(r.value).format('MM/DD/YYYY hh:mm A')}</Box>,
            },
            {
              field: 'updatedAt',
              headerName: 'Updated At',
              minWidth: 100,
              editable: false,
              renderCell: r => <Box>{dayjs(r.value).format('MM/DD/YYYY hh:mm A')}</Box>,
              flex: 1,
            },
          ]}
          renderAction={r => (
            <>
              <Tooltip placement="top" arrow title="Regenerate token">
                <IconButton
                  disabled={isButtonsDisabled}
                  size="small"
                  color="primary"
                  onClick={e => {
                    e.stopPropagation();
                    openDialog({
                      title: 'Renew',
                      content:
                        'By regenerating this API key, any request made using the old key will be rejected, which could cause systems depending on it to break. You will need to update any application or system using the old key with the new one. Are you sure you want to regenerate this API Key for your company?',
                      onConfirm: () => {
                        regenerateApikey(r.id);
                      },
                    });
                  }}
                >
                  <AutorenewIcon />
                </IconButton>
              </Tooltip>
              <IconButton
                disabled={isButtonsDisabled}
                size="small"
                color="primary"
                onClick={() => openEditModal(r.id)}
              >
                <EditIcon fontSize="inherit" />
              </IconButton>
              <IconButton
                disabled={isButtonsDisabled}
                size="small"
                color="primary"
                onClick={() =>
                  openDialog({
                    title: `Delete "${r.name}`,
                    content:
                      'By deleting this API key, any request made using this key will be rejected, which could cause systems depending on it to break. Once deleted, you will not be able to view or modify this key. Are you sure you want to remove this API Key from your company?',
                    onConfirm: () => removeApikey(r.id),
                  })
                }
              >
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            </>
          )}
          sorting={{
            value: sortField,
            onChange: (s: GridSortModel) => changeSorting(s),
          }}
          pagination={{
            page,
            pageSize: itemsPerPage,
            totalCount: apikeys.count,
            onChange: (p: GridPaginationModel) => changePagination(p),
          }}
        />
      </Box>
      <ConfirmationDialog />
    </>
  );
};
